<template>
  <el-dialog
    :title="$t('cart.inquiryTitle')"
    :visible.sync="dialogVisible"
    :width="dialogWidth"
    :close-on-click-modal="false"
    :modal-append-to-body="true"
    :append-to-body="true"
    custom-class="cart-dialog-panel"
    class="cart-dialog"
    @open="handleDialogOpen"
    @close="handleDialogClose"
  >
    <div v-loading="cartLoading" class="cart-content">
      <div v-if="cartList.length === 0" class="empty-cart">
        <i class="el-icon-shopping-cart-2"></i>
        <p>{{ $t('cart.empty') }}</p>
        <el-button type="primary" @click="dialogVisible = false">{{ $t('cart.goShopping') }}</el-button>
      </div>

      <div v-else class="cart-order-layout">
        <!-- 商品卡片 -->
        <section class="cart-card products-card">
          <div class="card-header">
            <span class="card-title">{{ $t('cart.inquiryTitle') }}</span>
            <el-button type="text" @click="handleClearCart" class="clear-btn">{{ $t('cart.clearInquiry') }}</el-button>
          </div>

          <div class="cart-list">
            <div
              class="cart-item"
              v-for="item in visibleCartList"
              :key="item.id"
            >
              <div class="item-image">
                <el-image
                  :src="getProductImage(item)"
                  :alt="item.productName"
                  :preview-src-list="[getProductImage(item)]"
                  fit="cover"
                  @error="handleImageError"
                >
                  <div slot="error" class="image-slot">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </el-image>
              </div>

              <div class="item-main">
                <div class="item-top">
                  <h4 class="item-name">{{ item.productName }}</h4>
                </div>
                <p class="item-sn" v-if="item.productSn">{{ item.productSn }}</p>
                <p class="item-spec" v-if="getDisplayProductAttr(item).length > 0">
                  <span v-for="(attr, index) in getDisplayProductAttr(item)" :key="index" class="attr-item">
                    <span class="attr-key">{{ attr.key }}:</span>
                    <span class="attr-value">{{ attr.value }}</span>
                    <span v-if="index < getDisplayProductAttr(item).length - 1" class="attr-separator">, </span>
                  </span>
                </p>
                <p class="item-brand" v-if="item.productBrand">
                  {{ $t('homePage.fits') }}：{{ formatCategoryHierarchyName(item.productBrand) }}
                </p>
                <div class="item-bottom">
                  <div class="side-qty-row">
                    <span v-if="getCartItemSide(item)" class="side-label">
                      {{ getCartItemSide(item) === 'L' ? $t('homePage.left') : $t('homePage.right') }}
                    </span>
                    <div class="product-qty-control">
                      <button
                        type="button"
                        class="qty-btn"
                        :disabled="item._updating"
                        @click="decreaseCartItem(item)"
                      >−</button>
                      <input
                        class="qty-value qty-input"
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        maxlength="3"
                        :value="item._qtyDraft != null ? item._qtyDraft : item.quantity"
                        :disabled="item._updating"
                        @focus="onQtyFocus(item)"
                        @input="onQtyInput(item, $event)"
                        @blur="commitQtyInput(item)"
                        @keyup.enter.prevent="$event.target.blur()"
                        @click.stop
                      >
                      <button
                        type="button"
                        class="qty-btn qty-btn-add"
                        :disabled="item._updating"
                        @click="increaseCartItem(item)"
                      >+</button>
                    </div>
                  </div>
                  <el-button
                    type="text"
                    icon="el-icon-delete"
                    @click="handleDeleteItem(item.id)"
                    class="delete-btn"
                  ></el-button>
                </div>
              </div>
            </div>
          </div>

          <button
            v-if="showExpandToggle"
            type="button"
            class="expand-toggle"
            @click="listExpanded = !listExpanded"
          >
            <template v-if="!listExpanded">
              {{ $t('cart.expandItems', { count: cartItemCount }) }}
              <i class="el-icon-arrow-down"></i>
            </template>
            <template v-else>
              {{ $t('cart.collapseItems') }}
              <i class="el-icon-arrow-up"></i>
            </template>
          </button>
        </section>

        <!-- 汇总卡片 -->
        <section class="cart-card summary-card">
          <div class="summary-row">
            <span>{{ $t('cart.selectedItems', { count: selectedCount }) }}</span>
            <span>{{ cartItemCount }} {{ $t('cart.piecesUnit') }}</span>
          </div>
          <div class="summary-row" v-if="totalCBM > 0">
            <span>{{ $t('cart.totalCbm') }}</span>
            <span class="summary-cbm">{{ totalCBMFormatted }}</span>
          </div>
          <div class="summary-row summary-total">
            <span>{{ $t('cart.totalAmount') }}</span>
            <span class="summary-amount">$ ?</span>
          </div>
        </section>
      </div>
    </div>

    <div v-if="cartList.length > 0" slot="footer" class="cart-sticky-footer">
      <el-button
        type="primary"
        class="footer-checkout-btn"
        @click="handleCheckout"
        :disabled="selectedCount === 0"
      >
        {{ $t('cart.checkout') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
  import { getCartList, updateQuantity, deleteCartItem, clearCart } from '@/api/cart';
  import { getToken } from '@/utils/auth';

  const DEFAULT_VISIBLE_COUNT = 3;

  export default {
    name: 'CartDialog',
    props: {
      visible: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        cartList: [],
        cartLoading: false,
        isMobile: false,
        listExpanded: false,
        defaultVisibleCount: DEFAULT_VISIBLE_COUNT
      };
    },
    computed: {
      dialogVisible: {
        get() {
          return this.visible;
        },
        set(val) {
          this.$emit('update:visible', val);
        }
      },
      dialogWidth() {
        return this.isMobile ? '96%' : '640px';
      },
      cartItemCount() {
        return this.cartList.reduce((total, item) => {
          return total + (parseInt(item.quantity, 10) || 1);
        }, 0);
      },
      showExpandToggle() {
        return this.cartList.length > this.defaultVisibleCount;
      },
      visibleCartList() {
        if (this.listExpanded || !this.showExpandToggle) {
          return this.cartList;
        }
        return this.cartList.slice(0, this.defaultVisibleCount);
      },
      selectedCount() {
        return this.cartList.filter(item => item.selected).length;
      },
      totalAmount() {
        return this.cartList
          .filter(item => item.selected)
          .reduce((total, item) => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseInt(item.quantity) || 1;
            return total + price * quantity;
          }, 0);
      },
      totalCBM() {
        const selectedItems = this.cartList.filter(item => item.selected);
        if (selectedItems.length === 0) return 0;

        let total = 0;
        selectedItems.forEach(item => {
          const cbm = this.getItemCBM(item);
          if (cbm) {
            total += parseFloat(cbm);
          }
        });

        return total;
      },
      totalCBMFormatted() {
        const total = this.totalCBM;
        return total > 0 ? total.toFixed(4) : '0.0000';
      }
    },
    created() {
      this.updateIsMobile();
    },
    mounted() {
      window.addEventListener('resize', this.updateIsMobile);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.updateIsMobile);
    },
    methods: {
      updateIsMobile() {
        this.isMobile = window.innerWidth <= 768;
      },
      formatCategoryHierarchyName(name) {
        if (name == null || name === '') return '';
        return String(name).replace(/\s*>\s*/g, ' ').trim();
      },
      handleDialogOpen() {
        this.listExpanded = false;
        this.loadCartList();
      },
      loadCartList() {
        if (!getToken()) {
          this.cartList = [];
          return Promise.resolve();
        }
        this.cartLoading = true;
        return getCartList()
          .then(res => {
            let list = [];
            if (res.code === 200 || res.code === 0) {
              if (Array.isArray(res.data)) {
                list = res.data;
              } else if (res.data && Array.isArray(res.data.list)) {
                list = res.data.list;
              } else if (res.data && Array.isArray(res.data.data)) {
                list = res.data.data;
              }
            } else if (Array.isArray(res)) {
              list = res;
            }
            this.cartList = list.map(item => ({
              ...item,
              selected: true,
              price: parseFloat(item.price) || 0,
              quantity: parseInt(item.quantity) || 1,
              _updating: false
            }));
            const cartCount = list.reduce((total, item) => {
              return total + (parseInt(item.quantity) || 1);
            }, 0);
            const cartData = {
              count: cartCount,
              list: list
            };
            this.$emit('cart-count-change', cartCount);
            this.$root.$emit('cart-count-change', cartData);
          })
          .catch(error => {
            console.error('获取询价单列表失败:', error);
            this.$message.error(this.$t('tip.loadInquiryListFailed'));
            this.cartList = [];
          })
          .finally(() => {
            this.cartLoading = false;
          });
      },
      getCartItemSide(item) {
        if (!item || !item.productAttr) return null;
        try {
          const attr = typeof item.productAttr === 'string'
            ? JSON.parse(item.productAttr)
            : item.productAttr;
          const side = attr && attr.Side ? String(attr.Side).toUpperCase() : null;
          return side === 'L' || side === 'R' ? side : null;
        } catch (e) {
          return null;
        }
      },
      // 展示用属性：过滤掉 Side，避免再显示 Side: L/R
      getDisplayProductAttr(item) {
        return this.getParsedProductAttr(item).filter(attr => {
          const key = String(attr.key || '').toLowerCase();
          return key !== 'side';
        });
      },
      increaseCartItem(item) {
        if (!item || item._updating) return;
        const quantity = (parseInt(item.quantity, 10) || 1) + 1;
        if (quantity > 999) return;
        this.$delete(item, '_qtyDraft');
        this.updateCartItemQuantity(item, quantity);
      },
      decreaseCartItem(item) {
        if (!item || item._updating) return;
        const quantity = parseInt(item.quantity, 10) || 1;
        this.$delete(item, '_qtyDraft');
        if (quantity > 1) {
          this.updateCartItemQuantity(item, quantity - 1);
          return;
        }
        this.handleDeleteItem(item.id);
      },
      onQtyFocus(item) {
        if (!item) return;
        this.$set(item, '_qtyDraft', String(parseInt(item.quantity, 10) || 1));
      },
      onQtyInput(item, event) {
        if (!item || !event || !event.target) return;
        const digits = String(event.target.value || '').replace(/\D/g, '').slice(0, 3);
        this.$set(item, '_qtyDraft', digits);
        event.target.value = digits;
      },
      commitQtyInput(item) {
        if (!item || item._updating) return;
        const draft = item._qtyDraft;
        this.$delete(item, '_qtyDraft');
        let quantity = parseInt(draft, 10);
        if (!Number.isFinite(quantity) || quantity < 1) {
          // 空/非法：恢复显示，不请求
          return;
        }
        if (quantity > 999) quantity = 999;
        const current = parseInt(item.quantity, 10) || 1;
        if (quantity === current) return;
        this.updateCartItemQuantity(item, quantity);
      },
      updateCartItemQuantity(item, quantity) {
        this.$set(item, '_updating', true);
        updateQuantity(item.id, quantity)
          .then(res => {
            if (res.code === 200 || res.code === 0) {
              this.$message.success(this.$t('tip.quantityUpdated'));
              return this.loadCartList();
            }
            this.$message.error(res.message || this.$t('tip.quantityUpdateFailed'));
            return this.loadCartList();
          })
          .catch(error => {
            console.error('更新数量失败:', error);
            this.$message.error(this.$t('tip.quantityUpdateFailedRetry'));
            this.loadCartList();
          })
          .finally(() => {
            this.$set(item, '_updating', false);
          });
      },
      handleDeleteItem(id) {
        this.$confirm(this.$t('cart.confirmDeleteItem'), this.$t('tip.notice'), {
          confirmButtonText: this.$t('tip.ok'),
          cancelButtonText: this.$t('tip.cancel'),
          type: 'warning'
        }).then(() => {
          deleteCartItem([id])
            .then(res => {
              if (res.code === 200 || res.code === 0) {
                this.$message.success(this.$t('tip.deleteSuccess'));
                this.loadCartList();
              } else {
                this.$message.error(res.message || this.$t('tip.deleteFailed'));
              }
            })
            .catch(error => {
              console.error('删除失败:', error);
              this.$message.error(this.$t('tip.deleteFailedRetry'));
            });
        }).catch(() => {});
      },
      handleClearCart() {
        this.$confirm(this.$t('tip.clearInquiryListConfirm'), this.$t('tip.notice'), {
          confirmButtonText: this.$t('tip.ok'),
          cancelButtonText: this.$t('tip.cancel'),
          type: 'warning'
        }).then(() => {
          clearCart()
            .then(res => {
              if (res.code === 200 || res.code === 0) {
                this.$message.success(this.$t('tip.clearInquiryListSuccess'));
                this.cartList = [];
                const cartData = { count: 0, list: [] };
                this.$emit('cart-count-change', 0);
                this.$root.$emit('cart-count-change', cartData);
              } else {
                this.$message.error(res.message || this.$t('tip.clearFailed'));
              }
            })
            .catch(error => {
              console.error('清空询价单失败:', error);
              this.$message.error(this.$t('tip.clearFailedRetry'));
            });
        }).catch(() => {});
      },
      handleCheckout() {
        if (this.selectedCount === 0) {
          this.$message.warning(this.$t('tip.selectItemsToCheckout'));
          return;
        }

        const selectedCartIds = this.cartList
          .filter(item => item.selected)
          .map(item => item.id);

        if (selectedCartIds.length === 0) {
          this.$message.warning(this.$t('tip.selectItemsToCheckout'));
          return;
        }

        this.dialogVisible = false;

        setTimeout(() => {
          this.$emit('checkout', selectedCartIds);
        }, 350);
      },
      getProductImage(product) {
        if (!product) {
          return 'https://via.placeholder.com/300x300/f5f5f5/999999?text=暂无图片';
        }

        if (product.productPic && product.productPic.trim() !== '') {
          return product.productPic.trim();
        }

        if (product.pic && product.pic.trim() !== '') {
          return product.pic.trim();
        }

        if (product.albumPics) {
          if (typeof product.albumPics === 'string' && product.albumPics.trim() !== '') {
            const pics = product.albumPics.split(',').map(p => p.trim()).filter(p => p);
            if (pics.length > 0) {
              return pics[0];
            }
          } else if (Array.isArray(product.albumPics) && product.albumPics.length > 0) {
            const firstPic = product.albumPics[0];
            if (firstPic && firstPic.trim() !== '') {
              return firstPic.trim();
            }
          }
        }

        return 'https://via.placeholder.com/300x300/f5f5f5/999999?text=暂无图片';
      },
      handleImageError(event) {
        if (event && event.target && event.target.src && !event.target.src.includes('placeholder')) {
          event.target.src = 'https://via.placeholder.com/300x300/f5f5f5/999999?text=暂无图片';
        }
      },
      handleDialogClose() {
        this.listExpanded = false;
      },
      getItemCBM(item) {
        if (!item || !item.productAttributeVoList || !Array.isArray(item.productAttributeVoList)) {
          return null;
        }

        const lengthAttr = this.findAttributeInList(item.productAttributeVoList, ['长度', '长', 'length', 'Length']);
        const widthAttr = this.findAttributeInList(item.productAttributeVoList, ['宽度', '宽', 'width', 'Width']);
        const heightAttr = this.findAttributeInList(item.productAttributeVoList, ['高度', '高', 'height', 'Height']);

        if (lengthAttr && widthAttr && heightAttr) {
          const length = this.parseNumber(lengthAttr.value);
          const width = this.parseNumber(widthAttr.value);
          const height = this.parseNumber(heightAttr.value);

          if (length > 0 && width > 0 && height > 0) {
            const singleCbm = (length * width * height) / 1000000;
            const singleCbmValue = this.ceilToDecimalPlaces(singleCbm, 4);
            const quantity = parseInt(item.quantity) || 1;
            const totalCbm = singleCbmValue * quantity;
            return totalCbm.toFixed(4);
          }
        }

        return null;
      },
      findAttributeInList(attributeList, possibleNames) {
        if (!attributeList || !Array.isArray(attributeList)) return null;

        for (const name of possibleNames) {
          const attr = attributeList.find(
            a => a.name && a.name.toLowerCase() === name.toLowerCase()
          );
          if (attr && attr.value && String(attr.value).trim() !== '') {
            return attr;
          }
        }

        return null;
      },
      parseNumber(value) {
        if (!value) return 0;
        const numStr = String(value).replace(/[^\d.]/g, '');
        const num = parseFloat(numStr);
        return isNaN(num) ? 0 : num;
      },
      ceilToDecimalPlaces(num, decimalPlaces) {
        const multiplier = Math.pow(10, decimalPlaces);
        return Math.ceil(num * multiplier) / multiplier;
      },
      getParsedProductAttr(item) {
        if (!item || !item.productAttr) {
          return [];
        }

        try {
          const attrObj = JSON.parse(item.productAttr);
          if (typeof attrObj === 'object' && attrObj !== null) {
            return Object.keys(attrObj).map(key => ({
              key: key,
              value: attrObj[key]
            }));
          }
        } catch (e) {
          if (item.productAttr && item.productAttr.trim() !== '') {
            return [{
              key: '',
              value: item.productAttr
            }];
          }
        }

        return [];
      }
    }
  };
</script>

<style scoped lang="scss">
.cart-dialog {
  ::v-deep .el-dialog__body {
    padding: 12px 16px 8px;
    background: #f5f6f8;
    max-height: 70vh;
    overflow-y: auto;
  }

  ::v-deep .el-dialog__footer {
    padding: 0;
    border-top: none;
  }

  .cart-content {
    min-height: 180px;
  }

  .empty-cart {
    text-align: center;
    padding: 60px 20px;
    color: #909399;
    background: #fff;
    border-radius: 12px;

    i {
      font-size: 72px;
      color: #c0c4cc;
      margin-bottom: 16px;
      display: block;
    }

    p {
      font-size: 15px;
      margin: 16px 0 24px;
    }
  }

  .cart-order-layout {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .cart-card {
    background: #fff;
    border-radius: 12px;
    padding: 14px 16px;
  }

  .products-card {
    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      padding-bottom: 10px;
      border-bottom: 1px solid #f0f0f0;
    }

    .card-title {
      font-size: 15px;
      font-weight: 700;
      color: #303133;
    }

    .clear-btn {
      color: #f56c6c;
      padding: 0;
      font-size: 13px;
    }
  }

  .cart-list {
    .cart-item {
      display: flex;
      gap: 12px;
      padding: 14px 0;
      border-bottom: 1px solid #f5f7fa;

      &:last-child {
        border-bottom: none;
      }
    }

    .item-image {
      width: 64px;
      height: 64px;
      flex-shrink: 0;
      border-radius: 8px;
      overflow: hidden;
      background: #f5f5f5;

      ::v-deep .el-image {
        width: 100%;
        height: 100%;

        .image-slot {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          height: 100%;
          background: #f5f5f5;
          color: #909399;
          font-size: 24px;
        }
      }
    }

    .item-main {
      flex: 1;
      min-width: 0;
    }

    .item-top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
    }

    .item-name {
      flex: 1;
      min-width: 0;
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      line-height: 1.35;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .item-sn {
      margin: 4px 0 0;
      font-size: 12px;
      color: #606266;
      line-height: 1.4;
    }

    .item-spec,
    .item-brand {
      margin: 4px 0 0;
      font-size: 12px;
      color: #909399;
      line-height: 1.4;
    }

    .item-spec {
      .attr-key {
        color: #606266;
      }

      .attr-separator {
        margin: 0 3px;
        color: #c0c4cc;
      }
    }

    .item-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 8px;
      gap: 8px;
    }

    .side-qty-row {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .side-label {
      font-size: 13px;
      font-weight: 700;
      color: #303133;
      line-height: 1;
    }

    .product-qty-control {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .qty-btn {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
      line-height: 1;
      cursor: pointer;
      padding: 0;
      background: #fff;
      border: 1.5px solid #DC143C;
      color: #303133;

      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      &.qty-btn-add {
        background: #DC143C;
        color: #fff;
        border-color: #DC143C;
        box-shadow: 0 2px 6px rgba(220, 20, 60, 0.28);
      }
    }

    .qty-value {
      min-width: 16px;
      text-align: center;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      line-height: 1;
    }

    .qty-input {
      width: 42px;
      min-width: 42px;
      height: 28px;
      padding: 0 4px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      background: #fff;
      box-sizing: border-box;
      outline: none;
      line-height: 28px;
      -moz-appearance: textfield;

      &:focus {
        border-color: #DC143C;
      }

      &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }

    .delete-btn {
      color: #c0c4cc;
      font-size: 16px;
      padding: 0;

      &:hover {
        color: #f56c6c;
      }
    }
  }

  .expand-toggle {
    width: 100%;
    margin-top: 4px;
    padding: 10px 0 2px;
    border: none;
    background: transparent;
    color: #909399;
    font-size: 13px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    i {
      font-size: 12px;
    }

    &:hover {
      color: #606266;
    }
  }

  .summary-card {
    .summary-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 0;
      font-size: 14px;
      color: #606266;

      & + .summary-row {
        border-top: 1px dashed #f0f0f0;
      }
    }

    .summary-cbm {
      color: #409EFF;
      font-weight: 600;
    }

    .summary-total {
      margin-top: 2px;
      font-weight: 600;
      color: #303133;
    }

    .summary-amount {
      color: #DC143C;
      font-size: 18px;
      font-weight: 700;
    }
  }

  .cart-sticky-footer {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    background: #fff;
    border-top: 1px solid #f0f0f0;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.04);
  }

  .footer-checkout-btn {
    display: block;
    width: 100%;
    height: 48px;
    line-height: 48px;
    border: none;
    border-radius: 24px;
    background: #DC143C;
    font-size: 16px;
    font-weight: 700;
    padding: 0;
    margin: 0;
  }
}

@media (max-width: 768px) {
  .cart-dialog {
    ::v-deep .el-dialog__body {
      padding: 10px 12px 8px;
      max-height: none;
    }

    .cart-card {
      padding: 12px;
      border-radius: 10px;
    }

    .cart-list {
      .item-image {
        width: 56px;
        height: 56px;
      }

      .item-name {
        font-size: 13px;
      }

      .item-spec,
      .item-brand {
        font-size: 11px;
      }
    }

    .footer-checkout-btn {
      height: 46px;
      line-height: 46px;
      font-size: 15px;
    }
  }
}
</style>

<style lang="scss">
.cart-dialog-panel {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    background: #fff;
    padding: 14px 16px 10px;
    border-bottom: 1px solid #f0f0f0;
  }

  .el-dialog__body {
    background: #f5f6f8;
  }

  .el-dialog__footer {
    padding: 0 !important;
  }

  // 覆盖全局移动端 dialog footer 按钮被压成细条的样式
  .cart-sticky-footer {
    display: block !important;
    width: 100% !important;
    flex-direction: unset !important;
    flex-wrap: unset !important;
    gap: 0 !important;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
  }

  .cart-sticky-footer .footer-checkout-btn.el-button,
  .cart-sticky-footer .el-button {
    display: block !important;
    flex: none !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    height: 48px !important;
    line-height: 48px !important;
    margin: 0 !important;
    padding: 0 !important;
    border-radius: 24px !important;
    font-size: 16px !important;
    font-weight: 700 !important;
  }
}

@media (max-width: 768px) {
  .cart-dialog-panel {
    width: 96% !important;
    margin: 2vh auto !important;
    max-height: 96vh;
    display: flex;
    flex-direction: column;
    border-radius: 12px;

    .el-dialog__body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding: 10px 12px 8px;
    }

    .el-dialog__footer {
      flex-shrink: 0;
      padding: 0 !important;
    }

    .cart-sticky-footer .footer-checkout-btn.el-button,
    .cart-sticky-footer .el-button {
      height: 46px !important;
      line-height: 46px !important;
      font-size: 15px !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CartDialog.vue